import React, { useEffect, useState, useRef } from "react";
import { withRouter } from "react-router-dom";
import Merchants from "./components/Merchants";
import Details from "./components/Details";
import ListDetailsSubLayout from "../../layout/ListDetailSubLayout";
import useMerchantStore from "../../stores/merchant";
import useCommonStore from "../../stores/common";
import { MonthPicker as CalendarFilter } from "../../components/form/month-picker";
import MonthPicker from "../../components/MonthPicker/Index";
import Modal from "../../components/modal";
import {
  parse,
  endOfMonth,
  format,
  startOfYear,
  endOfYear,
  subYears,
  startOfMonth,
} from "date-fns";
import ButtonIcon from "../../components/ButtonIcon/ButtonIcon";
import KebabDots16 from "../../assets/svg/icons/common/KebabDots16";
import StatementIcon16 from "../../assets/svg/icons/common/StatementIcon16";
import { getMerchantStatement } from "../../../api";
import toast from "react-hot-toast";
import style from "./Style.module.scss";

const MobileHeaderActions = () => {
  const setDateRange = useCommonStore((state) => state.setDateRange);
  const activeApp = useMerchantStore((state) => state.activeApp);
  const [showMenu, setShowMenu] = useState(false);
  const [showStatementPicker, setShowStatementPicker] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showMenu]);

  const handleMonthPickerChange = (data) => {
    let startDate, endDate;
    const currentDate = new Date();
    let rangeType = "MONTH";

    if (["MTD", "LTY", "YTD"].includes(data.month)) {
      rangeType = data.month;
      if (data.month === "YTD") {
        startDate = startOfYear(currentDate);
        endDate = currentDate;
      } else if (data.month === "MTD") {
        startDate = startOfMonth(currentDate);
        endDate = currentDate;
      } else if (data.month === "LTY") {
        const lastYearDate = subYears(currentDate, 1);
        startDate = startOfYear(lastYearDate);
        endDate = endOfYear(lastYearDate);
      }
    } else {
      const parsedDate = parse(
        `${data.month} ${data.year}`,
        "MMM yyyy",
        new Date(),
      );
      startDate = parsedDate;
      endDate = endOfMonth(parsedDate);
    }

    setDateRange({
      startDate: format(startDate, "yyyy-MM-dd"),
      endDate: format(endDate, "yyyy-MM-dd"),
      rangeType,
    });
  };

  const getFullMonthName = (shortMonth) => {
    const date = new Date(`${shortMonth} 1, 2020`);
    const options = { month: "long" };
    return new Intl.DateTimeFormat("en-US", options).format(date);
  };

  const handleDownload = (base64Pdf, file_name) => {
    const byteCharacters = Buffer.from(base64Pdf, "base64");
    const blob = new Blob([byteCharacters], { type: "application/pdf" });
    const url = window.URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = file_name;
    link.click();

    window.URL.revokeObjectURL(url);
  };

  const downloadStatement = (data) => {
    setShowStatementPicker(false);
    setShowMenu(false);

    const toastId = toast.loading(
      "Your statement is being run, do not close the page. The file will be downloaded automatically",
      { duration: 5 * 60 * 1000 },
    );

    getMerchantStatement({
      powerapp_id: activeApp.id,
      month: getFullMonthName(data.month),
      year: data.year,
    })
      .then((response) => {
        if (response.status) {
          handleDownload(
            response.data,
            `Merchant_Statement_${data.year}_${getFullMonthName(data.month)}.pdf`,
          );
          toast.success("Statement downloaded successfully", {
            id: toastId,
            duration: 30 * 1000,
          });
        } else {
          toast.error(response.message, { id: toastId, duration: 30 * 1000 });
        }
      })
      .catch(() => {
        toast.error("Something went wrong!", {
          id: toastId,
          duration: 30 * 1000,
        });
      });
  };

  const handleStatementClick = () => {
    setShowMenu(false);
    setShowStatementPicker(true);
  };

  return (
    <>
      <CalendarFilter
        variant="mobile"
        onSubmit={handleMonthPickerChange}
        changeOnSubmit
        showYearFilters
        showActions
      />
      <div className={style.mobile_menu_wrapper} ref={menuRef}>
        <ButtonIcon
          bgColor="#EDEDF4"
          className={style.mobile_header_menu_button}
          onClick={() => setShowMenu(!showMenu)}
        >
          <KebabDots16 />
        </ButtonIcon>

        {showMenu && (
          <div className={style.mobile_dropdown_menu}>
            <button
              className={style.mobile_menu_item}
              onClick={handleStatementClick}
            >
              <StatementIcon16 />
              <span>Statement</span>
            </button>
          </div>
        )}
      </div>

      {showStatementPicker && (
        <Modal
          isOpen={showStatementPicker}
          onClose={() => setShowStatementPicker(false)}
          header="Download Statement"
          size="xs"
        >
          <MonthPicker
            alwaysOpen={true}
            onSubmit={downloadStatement}
            submitText="Download PDF"
            onClose={() => setShowStatementPicker(false)}
            showActions={true}
          />
        </Modal>
      )}
    </>
  );
};

const MerchantPage = ({ location }) => {
  const deepLinkAppliedRef = useRef(null);

  useEffect(() => {
    return () => {
      useCommonStore.getState().reset();
      useMerchantStore.getState().reset();
    };
  }, []);

  useEffect(() => {
    const idParam = new URLSearchParams(location.search)?.get("id");
    if (!idParam) {
      deepLinkAppliedRef.current = null;
      return;
    }

    const powerappId = Number(idParam);
    if (!Number.isFinite(powerappId) || powerappId <= 0) {
      toast.error("Invalid merchant link.");
      return;
    }

    const marker = `${location.pathname}?id=${powerappId}`;
    if (deepLinkAppliedRef.current === marker) return;
    deepLinkAppliedRef.current = marker;

    useMerchantStore.getState().setActiveAppId(powerappId);
    useCommonStore.getState().setShowDetails(true);
    useCommonStore.getState().setExpandDetails(true);
  }, [location.pathname, location.search]);

  return (
    <ListDetailsSubLayout
      detailsComponent={Details}
      listComponent={Merchants}
      mobileHeaderActions={<MobileHeaderActions />}
    />
  );
};

export default withRouter(MerchantPage);
